<template>
  <div
    class="relative sticky top-0 z-10 min-h-55px flex items-center justify-between bg-white"
    :class="{ 'h-55px px-20px': isMobileStyle, 'h-80px px-60px': !isMobileStyle }"
    :value="modelValue"
  >
    <SideModal
      v-model:visible="isMenuOpen"
      class="h-100dvh gap-24px py-36px title-14-h16-m text-primary-100"
    >
      <i class="icon-logo text-24px" />
      <hr class="border-primary-80 rounded-full" />
      <p
        v-for="(item, index) in sidebarList"
        :key="index"
        class="cursor-pointer"
        @click="closeMenuAndGo(item.redirect)"
      >
        {{ item.title }}
        <i v-if="item.star" class="icon-star ml-16px text-16px" />
      </p>
    </SideModal>
    <div class="flex-1" :class="{ hidden: !isMobileStyle }">
      <i
        class="icon-hamburger-three-line cursor-pointer text-24px text-primary-80"
        @click="isMenuOpen = true"
      />
    </div>
    <div
      class="flex-2 ml-4px h-24px inline-flex cursor-pointer items-center"
      @click="$router.push(Route.Root)"
    >
      <i class="icon-logo text-12px" />
      <p class="mb-2px ml-8px inline title-14-h16-m">{{ $t('header.title') }}</p>
    </div>
    <div class="ml-36px">
      <AuInputSearch
        v-if="!isMobileStyle && !hideSearchBar"
        v-model:model-value="modelValue"
        class="w-360px"
        :placeholder="$t('search_bar.placeholder')"
        @clear="
          async () => {
            await keywordSearch('')
          }
        "
        @search="
          async (text: string) => {
            await keywordSearch(text)
          }
        "
      />
    </div>
    <div class="flex flex-1 items-center justify-end gap-36px">
      <p
        v-for="(item, index) in headerList"
        :key="index"
        class="cursor-pointer title-14-h16-m text-primary-100"
        :class="{
          hidden: isMobileStyle,
        }"
        @click="typeof item.redirect === 'function' ? item.redirect() : router.push(item.redirect)"
      >
        {{ item.title }}
      </p>
      <div class="flex items-center gap-24px md:gap-36px">
        <i
          v-if="layoutStore.isDesktop"
          class="icon-home cursor-pointer text-24px"
          @click="redirectOfficialPage"
        />
        <i
          v-if="layoutStore.isDesktop"
          ref="heartIconElement"
          class="icon-heart cursor-pointer text-24px"
          @click="
            async () => {
              pushGA4ButtonClick('favorite artwork redirect: icon')
              await $router.push(Route.FavoriteArtworkPage)
            }
          "
        />
        <i
          v-if="isMobileStyle"
          class="icon-search cursor-pointer text-24px"
          @click.stop="
            async () => {
              showSearchPanel = true
            }
          "
        />
        <CartIconWithQuantity />
      </div>
    </div>
    <div
      v-if="showSearchPanel"
      v-click-outside="
        () => {
          showSearchPanel = false
        }
      "
      class="absolute left-0 top-0 w-full flex flex-col items-center bg-white shadow-fix-shadow-top"
      :value="modelValue"
    >
      <div
        class="relative w-full flex justify-center justify-center pb-7px pt-8px shadow-fix-shadow-top"
      >
        <AuInputSearch
          v-model:model-value="modelValue"
          class="w-263px"
          :placeholder="$t('search_bar.placeholder_mobile')"
          @clear="
            async () => {
              await keywordSearch('')
            }
          "
          @search="
            async (text: string) => {
              await keywordSearch(text)
            }
          "
        />
        <i
          class="icon-chevron-left absolute left-0 ml-20px mt-8px cursor-pointer text-24px text-primary-80"
          @click="showSearchPanel = false"
        />
      </div>
      <div
        v-for="(term, index) in popularList"
        :key="index"
        class="w-full flex cursor-pointer justify-center py-12px hover:bg-light-gray"
        @click="keywordSearch(term)"
      >
        <div class="content-12-h12-r w-230px flex justify-between text-primary-70">
          <p>
            {{ term }}
          </p>
          <i class="icon-search text-24px" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AuInputSearch } from '@evolutivelabs/amuse-ui'
import { t } from 'i18next'
import { computed, ref, useTemplateRef, watch } from 'vue'
import { useRouter } from 'vue-router'

import SideModal from '@/components/layout/SideModal.vue'
import { Route } from '@/router/route'
import { useLayoutStore } from '@/store/layoutStore'
import { popularList } from '@/utils/common'
import { pushGA4ButtonClick, pushGA4ViewCart } from '@/utils/googleAnalytics'
import { redirectOfficialCartPage, redirectOfficialPage } from '@/utils/redirect'

import CartIconWithQuantity from './CartIconWithQuantity.vue'

const isMenuOpen = ref(false)
const router = useRouter()
const layoutStore = useLayoutStore()
const isMobileStyle = computed(() => {
  return layoutStore.isMobile || !layoutStore.isRwdPage
})

const modelValue = defineModel('keyword', { type: String, default: '' })
const showSearchPanel = defineModel('showSearchPanel', { type: Boolean, default: false })
const HEART_ANIMATION_DURATION = 500
const heartIconElement = useTemplateRef<HTMLElement>('heartIconElement')

interface Props {
  hideSearchBar?: boolean
  useEmitSearch?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  hideSearchBar: false,
  useEmitSearch: false,
})

const emit = defineEmits<{
  search: [keyword: string]
}>()

async function keywordSearch(keyword: string): Promise<void> {
  showSearchPanel.value = false
  if (props.useEmitSearch) {
    emit('search', keyword)
  } else {
    await router.push({ path: Route.GalleryPage, query: { keyword: encodeURIComponent(keyword) } })
  }
}

async function closeMenuAndGo(event: (() => void) | Route): Promise<void> {
  isMenuOpen.value = false
  if (typeof event === 'function') {
    event()
  } else {
    await router.push(event)
  }
}

watch(
  () => layoutStore.animateHeartTriggerSignal,
  () => {
    heartAnimate()
  },
)
function heartAnimate(): void {
  const target = heartIconElement.value
  if (target === null) return
  target.classList.remove('heart-animation', 'icon-heart')
  // css reflow
  void target.offsetWidth
  target.classList.add('heart-animation', 'icon-heart-fill', 'text-brand-blushPink')
  window.setTimeout(() => {
    target.classList.remove('heart-animation', 'icon-heart-fill', 'text-brand-blushPink')
    target.classList.add('icon-heart')
  }, HEART_ANIMATION_DURATION)
}

const sidebarList: Array<{
  redirect: (() => Promise<void> | void) | Route
  star?: true
  title: string
}> = [
  {
    title: t('header.free_design'),
    redirect: async () => {
      pushGA4ButtonClick('design redirect: sidebar')
      await router.push(Route.DesignPage)
    },
    star: true,
  },
  {
    title: t('header.gallery'),
    redirect: async () => {
      pushGA4ButtonClick('gallery redirect: sidebar')
      await router.push(Route.GalleryPage)
    },
  },
  {
    title: t('header.collaborating_artists'),
    redirect: async () => {
      pushGA4ButtonClick('artists redirect: sidebar')
      await router.push(Route.ArtistsPage)
    },
  },
  {
    title: t('header.favorite'),
    redirect: async () => {
      pushGA4ButtonClick('favorite artwork redirect: sidebar')
      await router.push(Route.FavoriteArtworkPage)
    },
  },
  {
    title: t('header.cart'),
    redirect: () => {
      pushGA4ViewCart()
      redirectOfficialCartPage()
    },
  },
  {
    title: t('header.website'),
    redirect: () => {
      pushGA4ButtonClick('official button icon')
      redirectOfficialPage()
    },
  },
]

const headerList: Array<{
  redirect: (() => Promise<void> | void) | Route
  title: string
}> = [
  {
    title: t('header.free_design'),
    redirect: async () => {
      pushGA4ButtonClick('design redirect: header title')
      await router.push(Route.DesignPage)
    },
  },
  {
    title: t('header.gallery'),
    redirect: async () => {
      pushGA4ButtonClick('gallery redirect: header title')
      await router.push(Route.GalleryPage)
    },
  },
  {
    title: t('header.collaborating_artists'),
    redirect: async () => {
      pushGA4ButtonClick('artists redirect: header title')
      await router.push(Route.ArtistsPage)
    },
  },
]
</script>

<style scoped>
.heart-animation {
  animation-name: heart-size;
  animation-duration: v-bind(HEART_ANIMATION_DURATION + 'ms');
}
@keyframes heart-size {
  0% {
    transform: scale(1);
  }
  20% {
    transform: scale(3);
  }
  100% {
    transform: scale(1);
  }
}
</style>
